/**
 * B5-3: CSRF double-submit token — client side.
 *
 * The Worker's global middleware refuses POST/PUT/PATCH/DELETE to
 * `/api/*` (except `/api/auth/*`, `/api/health`, and the webhooks)
 * unless the request carries `X-CSRF-Token: <cookie value>`. The
 * cookie itself (`wos_csrf`) is set at /api/auth/callback with
 * `SameSite=Strict; Secure` but WITHOUT `HttpOnly` so this module
 * can read it via `document.cookie` and echo it back.
 *
 * We install a global `fetch` interceptor once at app boot so every
 * state-changing call to `/api/*` picks up the header automatically —
 * including the many scattered `await fetch('/api/...', {method:'POST'})`
 * sites throughout the pages. This keeps the invariant "if it's a
 * same-origin write, it carries CSRF" in one place instead of
 * spread across every page that mutates state.
 *
 * The interceptor is a NARROW wrap: it only touches same-origin
 * `/api/*` state-changing calls. Everything else (GETs, cross-origin
 * fetches, non-API calls) flows through unchanged.
 */

const CSRF_COOKIE_NAME = 'wos_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  if (!raw) return null;
  for (const chunk of raw.split(';')) {
    const [k, ...rest] = chunk.trim().split('=');
    if (k === CSRF_COOKIE_NAME) return rest.join('=') || null;
  }
  return null;
}

function isStateChanging(method: string | undefined | null): boolean {
  if (!method) return false;
  const m = method.toUpperCase();
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
}

/**
 * Same-origin `/api/*` — excluding `/api/auth/*` which bootstraps the
 * cookie (the OAuth callback is a top-level GET) and excluding
 * webhooks (they authenticate via signature, not session cookie).
 *
 * Path check is on the URL's pathname so query strings and hashes
 * don't confuse it. Cross-origin requests always fall through — we
 * MUST NOT leak our CSRF token to third parties.
 */
function shouldInjectCsrf(url: URL): boolean {
  if (typeof location === 'undefined') return false;
  if (url.origin !== location.origin) return false;
  const p = url.pathname;
  if (!p.startsWith('/api/')) return false;
  if (p.startsWith('/api/auth/')) return false;
  if (p.startsWith('/api/billing/webhook')) return false;
  return true;
}

function resolveUrl(input: RequestInfo | URL): URL | null {
  try {
    if (typeof input === 'string') return new URL(input, location.href);
    if (input instanceof URL) return input;
    // Request object
    return new URL(input.url, location.href);
  } catch {
    return null;
  }
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method;
  if (typeof input !== 'string' && !(input instanceof URL)) {
    return input.method || 'GET';
  }
  return 'GET';
}

let installed = false;

/**
 * Wrap the global `fetch` so state-changing calls to same-origin
 * `/api/*` (minus auth + webhooks) auto-inject X-CSRF-Token. Safe to
 * call multiple times — subsequent calls are no-ops.
 *
 * The wrap preserves the original function on `window.fetch.__unwrapped`
 * so tests / dev tools can inspect it if needed.
 */
export function installCsrfFetchInterceptor(): void {
  if (installed) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  const wrapped: typeof fetch = async (input, init) => {
    const url = resolveUrl(input);
    if (!url) return originalFetch(input, init);
    const method = resolveMethod(input, init);
    if (!isStateChanging(method) || !shouldInjectCsrf(url)) {
      return originalFetch(input, init);
    }
    const token = readCsrfCookie();
    if (!token) {
      // No token — no session, no CSRF check would be run server-side
      // anyway (unauthenticated → 401 before CSRF check fires). Let
      // the request through so the caller sees the real 401.
      return originalFetch(input, init);
    }
    // Merge the header while preserving whatever the caller passed.
    const nextInit: RequestInit = { ...(init ?? {}) };
    const headers = new Headers(nextInit.headers ?? undefined);
    // If the input was a Request, its headers ride along too — Headers
    // constructor merges only what's in init, so we also copy from
    // the Request. This mirrors what fetch does internally when both
    // are set (init wins per-header).
    if (typeof input !== 'string' && !(input instanceof URL)) {
      input.headers.forEach((v, k) => {
        if (!headers.has(k)) headers.set(k, v);
      });
    }
    headers.set(CSRF_HEADER_NAME, token);
    nextInit.headers = headers;
    return originalFetch(input, nextInit);
  };

  (wrapped as typeof fetch & { __unwrapped?: typeof fetch }).__unwrapped =
    originalFetch;
  window.fetch = wrapped;
}
